import React, { useState } from "react";
import { TrainFront, ArrowRight, MapPin, Calendar, ArrowLeftRight, Clock, Search } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Label } from "./ui/label";
import { POPULAR_TRAINS, TRAIN_CLASSES } from "../mock/mock";
import { useToast } from "../hooks/use-toast";

const TrainSearch = () => {
  const { toast } = useToast();
  const today = new Date().toISOString().slice(0, 10);
  const [q, setQ] = useState({ from: "", to: "", date: today });
  const [results, setResults] = useState(POPULAR_TRAINS);
  const [selected, setSelected] = useState(null);
  const [pax, setPax] = useState({ name: "", phone: "", cls: "3A" });
  const [booked, setBooked] = useState(null);

  const search = (e) => {
    e.preventDefault();
    if (!q.from || !q.to) {
      toast({ title: "Please add stations", description: "Enter both origin and destination." });
      return;
    }
    const filtered = POPULAR_TRAINS.filter(
      (t) =>
        t.from.toLowerCase().includes(q.from.toLowerCase()) ||
        t.to.toLowerCase().includes(q.to.toLowerCase())
    );
    setResults(filtered.length ? filtered : POPULAR_TRAINS);
    toast({
      title: `${filtered.length || POPULAR_TRAINS.length} trains found`,
      description: `${q.from || "Anywhere"} → ${q.to || "Anywhere"} on ${q.date}`,
    });
  };

  const swap = () => setQ({ ...q, from: q.to, to: q.from });

  const book = (e) => {
    e.preventDefault();
    if (!pax.name || !pax.phone) {
      toast({ title: "Passenger details required", description: "Fill in name and phone to proceed." });
      return;
    }
    setBooked({
      pnr: "PNR" + Date.now().toString().slice(-9),
      ...selected,
      cls: pax.cls,
      name: pax.name,
    });
  };

  const closeDialog = () => {
    setSelected(null);
    setBooked(null);
    setPax({ name: "", phone: "", cls: "3A" });
  };

  return (
    <section id="trains" className="py-20 md:py-28 bg-gradient-to-b from-white to-slate-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-100 text-teal-700 text-xs font-semibold tracking-wider uppercase">
            <TrainFront className="w-3.5 h-3.5" /> IRCTC Authorised Agent
          </div>
          <h2 className="mt-4 text-3xl md:text-5xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Book train tickets in seconds
          </h2>
          <p className="mt-4 text-slate-600 text-lg">
            Search live trains, book Tatkal, and get confirmed berth guidance from our IRCTC-certified experts.
          </p>
        </div>

        {/* Search form */}
        <form
          onSubmit={search}
          className="mt-10 bg-white rounded-2xl shadow-xl shadow-slate-900/5 border border-slate-100 p-3 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_1fr_auto] gap-2 items-center"
        >
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-50">
            <MapPin className="w-5 h-5 text-teal-600" />
            <div className="flex-1">
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">From</label>
              <Input
                value={q.from}
                onChange={(e) => setQ({ ...q, from: e.target.value })}
                placeholder="Chennai"
                className="border-0 p-0 h-6 focus-visible:ring-0 shadow-none text-slate-900 placeholder:text-slate-400"
              />
            </div>
          </div>
          <button
            type="button"
            onClick={swap}
            className="hidden md:flex w-10 h-10 rounded-full bg-slate-100 hover:bg-teal-600 hover:text-white items-center justify-center text-slate-600 transition"
            aria-label="swap"
          >
            <ArrowLeftRight className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-50 border-t md:border-t-0 md:border-l border-slate-100">
            <MapPin className="w-5 h-5 text-orange-500" />
            <div className="flex-1">
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">To</label>
              <Input
                value={q.to}
                onChange={(e) => setQ({ ...q, to: e.target.value })}
                placeholder="Bengaluru"
                className="border-0 p-0 h-6 focus-visible:ring-0 shadow-none text-slate-900 placeholder:text-slate-400"
              />
            </div>
          </div>
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-50 border-t md:border-t-0 md:border-l border-slate-100">
            <Calendar className="w-5 h-5 text-teal-600" />
            <div className="flex-1">
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">Journey Date</label>
              <Input
                type="date"
                value={q.date}
                onChange={(e) => setQ({ ...q, date: e.target.value })}
                className="border-0 p-0 h-6 focus-visible:ring-0 shadow-none text-slate-900"
              />
            </div>
          </div>
          <Button type="submit" className="h-14 md:h-auto md:aspect-square md:w-14 bg-teal-600 hover:bg-teal-700 rounded-xl" aria-label="search">
            <Search className="w-5 h-5" />
            <span className="md:hidden ml-2">Search Trains</span>
          </Button>
        </form>

        {/* Popular classes chips */}
        <div className="mt-6 flex flex-wrap gap-2">
          <span className="text-sm text-slate-500 self-center mr-1">Class options:</span>
          {TRAIN_CLASSES.map((c) => (
            <Badge key={c.code} variant="outline" className="border-teal-200 text-teal-700 bg-teal-50 font-medium">
              {c.code} · {c.label}
            </Badge>
          ))}
        </div>

        {/* Results */}
        <div className="mt-10">
          <div className="flex items-end justify-between mb-4">
            <h3 className="text-xl font-bold text-slate-900">Popular Trains</h3>
            <span className="text-sm text-slate-500">{results.length} results</span>
          </div>
          <div className="space-y-3">
            {results.map((t) => (
              <div
                key={t.no}
                className="group bg-white rounded-xl border border-slate-200 hover:border-teal-500 hover:shadow-lg transition p-5 grid grid-cols-1 md:grid-cols-[auto_1fr_auto_auto] gap-4 md:gap-6 items-center"
              >
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center">
                    <TrainFront className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-[11px] font-mono text-slate-500">#{t.no}</div>
                    <div className="font-bold text-slate-900">{t.name}</div>
                  </div>
                </div>
                <div className="flex items-center gap-4 md:gap-6">
                  <div>
                    <div className="text-xl font-bold text-slate-900 leading-none">{t.dep}</div>
                    <div className="text-xs text-slate-500 mt-1">{t.from}</div>
                  </div>
                  <div className="flex-1 flex flex-col items-center">
                    <div className="text-[11px] text-slate-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {t.duration}
                    </div>
                    <div className="w-full h-px bg-slate-200 relative my-1">
                      <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-teal-600" />
                    </div>
                    <div className="text-[11px] text-slate-400">Direct</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-bold text-slate-900 leading-none">{t.arr}</div>
                    <div className="text-xs text-slate-500 mt-1">{t.to}</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 justify-end">
                  {t.classes.map((c) => (
                    <span key={c} className="text-[10px] font-bold text-teal-700 bg-teal-50 border border-teal-200 rounded px-1.5 py-0.5">
                      {c}
                    </span>
                  ))}
                </div>
                <div className="text-right">
                  <div className="text-[11px] text-slate-500">from</div>
                  <div className="text-xl font-bold text-teal-700">₹{t.fromPrice.toLocaleString("en-IN")}</div>
                  <Button
                    onClick={() => setSelected(t)}
                    className="mt-2 bg-orange-500 hover:bg-orange-600 text-white h-9"
                  >
                    Book <ArrowRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => !v && closeDialog()}>
        <DialogContent className="max-w-lg">
          {!booked && selected && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  Book {selected.name}
                </DialogTitle>
                <DialogDescription>
                  #{selected.no} · {selected.from} → {selected.to} · {selected.dep} - {selected.arr}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={book} className="mt-4 space-y-4">
                <div>
                  <Label htmlFor="tname" className="text-xs">Passenger Name *</Label>
                  <Input id="tname" value={pax.name} onChange={(e) => setPax({ ...pax, name: e.target.value })} placeholder="Ravi Kumar" />
                </div>
                <div>
                  <Label htmlFor="tphone" className="text-xs">Phone *</Label>
                  <Input id="tphone" value={pax.phone} onChange={(e) => setPax({ ...pax, phone: e.target.value })} placeholder="+91 98765 43210" />
                </div>
                <div>
                  <Label className="text-xs">Travel Class</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {selected.classes.map((c) => (
                      <button
                        type="button"
                        key={c}
                        onClick={() => setPax({ ...pax, cls: c })}
                        className={`px-3 py-1.5 rounded-md text-sm font-semibold border transition ${
                          pax.cls === c
                            ? "bg-teal-600 text-white border-teal-600"
                            : "bg-white text-slate-700 border-slate-200 hover:border-teal-400"
                        }`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="pt-2 border-t border-slate-100 flex items-end justify-between">
                  <div>
                    <div className="text-xs text-slate-500">Total (approx.)</div>
                    <div className="text-2xl font-bold text-slate-900">₹{selected.fromPrice.toLocaleString("en-IN")}</div>
                  </div>
                  <Button type="submit" className="bg-teal-600 hover:bg-teal-700">
                    Confirm & Pay
                  </Button>
                </div>
              </form>
            </>
          )}

          {booked && (
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-green-100 mx-auto flex items-center justify-center">
                <TrainFront className="w-9 h-9 text-green-600" />
              </div>
              <h3 className="mt-5 text-2xl font-bold text-slate-900" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Ticket Booked!
              </h3>
              <p className="mt-2 text-slate-600 text-sm">
                Your e-ticket has been sent via SMS & email.
              </p>
              <div className="mt-5 rounded-lg bg-slate-50 border border-slate-200 p-4 text-sm text-left space-y-1">
                <div className="flex justify-between"><span className="text-slate-500">PNR</span><span className="font-mono font-semibold">{booked.pnr}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Train</span><span className="font-semibold">{booked.name}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Class</span><span className="font-semibold">{booked.cls}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Passenger</span><span className="font-semibold">{booked.name}</span></div>
              </div>
              <Button onClick={closeDialog} className="mt-6 bg-teal-600 hover:bg-teal-700">Done</Button>
              <p className="mt-4 text-[11px] text-orange-600 font-semibold">DEMO MODE — Booking is MOCKED (no real IRCTC transaction).</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default TrainSearch;
